import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { playerSidebar } from '@/config/sidebarConfig.ts';
import { createMembershipRequestApi, getClubApi, getClubsApi, getMyMembershipRequestApi } from '@/lib/api/clubsApi';
import { getUsersApi } from '@/lib/api/usersApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr, getInitials } from '@shared';
import type { Club } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  Mail,
  MapPin,
  Phone,
  Search,
  Shield,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../components/layout/DashboardLayout';
import Dialog from '../../components/ui/Dialog';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';

const FRONT_URL = import.meta.env.VITE_URL_FRONT as string;
const PAGE_SIZE = 9;

// ─── Sub-components ──────────────────────────────────────────

function MemberAvatar({
  firstName,
  lastName,
  index,
}: {
  firstName: string | null;
  lastName: string | null;
  index: number;
}) {
  const gradients = [
    'from-orange-500 to-red-600',
    'from-emerald-500 to-green-600',
    'from-blue-500 to-indigo-600',
    'from-purple-500 to-violet-600',
    'from-pink-500 to-rose-600',
  ];
  return (
    <div
      className={`flex h-9 w-9 items-center justify-center rounded-full bg-linear-to-br ${gradients[index % gradients.length]} text-xs font-bold text-white`}
    >
      {getInitials(firstName, lastName)}
    </div>
  );
}

// ─── State A: Dans un club ────────────────────────────────────

function MyClubView({ clubId }: { clubId: number }) {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  const {
    data: club,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.clubs.detail(clubId),
    queryFn: () => getClubApi(clubId),
    enabled: isAuthenticated,
  });

  const { data: membersData } = useQuery({
    queryKey: queryKeys.users.list({ clubId, limit: 6 }),
    queryFn: () => getUsersApi({ clubId, limit: 6 }),
    enabled: isAuthenticated,
  });

  const members = membersData?.data ?? [];
  const totalMembers = membersData?.meta?.total ?? 0;

  if (isError) {
    return <QueryErrorState onRetry={refetch} />;
  }

  if (isLoading || !club) {
    return (
      <div className='space-y-4'>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className='h-32 animate-pulse rounded-2xl bg-slate-200' />
        ))}
      </div>
    );
  }

  return (
    <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
      {/* Main */}
      <div className='space-y-6 lg:col-span-2'>
        {/* Club header card */}
        <div className='rounded-2xl border border-slate-200 bg-white p-6'>
          <div className='mb-6 flex items-start gap-5'>
            <div className='flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-linear-to-br from-emerald-500 to-green-600 text-xl font-extrabold text-white'>
              {club.name.slice(0, 2).toUpperCase()}
            </div>
            <div className='flex-1 min-w-0'>
              <div className='flex items-start justify-between gap-3'>
                <div>
                  <h2 className='text-xl font-extrabold text-slate-900'>{club.name}</h2>
                  {club.alias && <p className='text-sm text-slate-500'>@{club.alias}</p>}
                </div>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    club.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {club.isActive ? 'Actif' : 'Inactif'}
                </span>
              </div>
              <div className='mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-500'>
                {(club.city || club.department) && (
                  <span className='flex items-center gap-1'>
                    <MapPin className='h-3.5 w-3.5' />
                    {[club.city, club.department].filter(Boolean).join(', ')}
                  </span>
                )}
                <span className='flex items-center gap-1'>
                  <Users className='h-3.5 w-3.5' />
                  {totalMembers} membre{totalMembers > 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>

          {club.description && <p className='mb-5 text-sm leading-relaxed text-slate-600'>{club.description}</p>}

          <div className='flex flex-wrap gap-3 border-t border-slate-100 pt-5'>
            {/*
            TODO mail form contact for club
            club.email && (
              <a
                href={`mailto:${club.email}`}
                className='flex items-center gap-1.5 text-sm text-slate-600 hover:text-orange-600 transition-colors'
              >
                <Mail className='h-4 w-4' />
                {club.email}
              </a>
            )*/}
            {club.phone && (
              <a
                href={`tel:${club.phone}`}
                className='flex items-center gap-1.5 text-sm text-slate-600 hover:text-orange-600 transition-colors'
              >
                <Phone className='h-4 w-4' />
                {club.phone}
              </a>
            )}
            <a
              href={`${FRONT_URL}/clubs/${club.id}`}
              target='_blank'
              rel='noopener noreferrer'
              className='ml-auto flex items-center gap-1.5 rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200'
            >
              <ExternalLink className='h-4 w-4' />
              Fiche du club
            </a>
          </div>
        </div>

        {/* Mon statut */}
        <div className='rounded-2xl bg-linear-to-br from-orange-600 to-red-600 p-6 text-white'>
          <h3 className='mb-4 flex items-center gap-2 font-bold'>
            <Shield className='h-5 w-5' />
            Mon statut dans le club
          </h3>
          <div className='grid grid-cols-3 gap-4'>
            <div className='rounded-xl bg-white/10 p-4 backdrop-blur'>
              <p className='mb-1 text-xs text-orange-100'>Rôle</p>
              <p className='font-bold'>{user?.isClubAdmin ? 'Admin' : 'Membre'}</p>
            </div>
            <div className='rounded-xl bg-white/10 p-4 backdrop-blur'>
              <p className='mb-1 text-xs text-orange-100'>Licence</p>
              <p className='font-bold truncate'>
                {user?.licenseNumber ?? <span className='font-normal text-orange-200'>—</span>}
              </p>
            </div>
            <div className='rounded-xl bg-white/10 p-4 backdrop-blur'>
              <p className='mb-1 text-xs text-orange-100'>Membre depuis</p>
              <p className='font-bold text-sm'>{user?.createdAt ? formatDateAbbreviatedFr(user.createdAt) : '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar membres */}
      <div>
        <div className='rounded-2xl border border-slate-200 bg-white p-6'>
          <h3 className='mb-4 flex items-center gap-2 font-bold text-slate-900'>
            <Users className='h-5 w-5 text-slate-500' />
            Membres
            <span className='ml-auto text-sm font-normal text-slate-400'>{totalMembers} total</span>
          </h3>
          <div className='space-y-2'>
            {members.map((m, i) => (
              <div key={m.id} className='flex items-center gap-3 rounded-xl p-2 transition hover:bg-slate-50'>
                <MemberAvatar firstName={m.firstName} lastName={m.lastName} index={i} />
                <div className='min-w-0 flex-1'>
                  <p className='truncate text-sm font-semibold text-slate-900'>
                    {m.firstName} {m.lastName}
                  </p>
                  <p className='text-xs text-slate-500'>@{m.username}</p>
                </div>
                {m.isClubAdmin && <Shield className='h-3.5 w-3.5 shrink-0 text-purple-500' />}
              </div>
            ))}
          </div>
          {totalMembers > 6 && (
            <p className='mt-3 text-center text-xs text-slate-400'>+{totalMembers - 6} autres membres</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── State B: Pas de club ─────────────────────────────────────

function ClubSearchView() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') ?? '1', 10);
  const search = searchParams.get('search') ?? '';
  const cityFilter = searchParams.get('city') ?? '';

  const setParam = (key: string, value: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  const [searchInput, setSearchInput] = useState(search);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [clubToJoin, setClubToJoin] = useState<Club | null>(null);
  const [message, setMessage] = useState('');

  // Debounce search input -> URL param
  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (searchInput) next.set('search', searchInput);
          else next.delete('search');
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    }, 350);
    return () => clearTimeout(searchTimeout.current);
  }, [searchInput, setSearchParams]);

  // Ma demande en attente
  const { data: pendingRequest } = useQuery({
    queryKey: queryKeys.clubs.myMembershipRequest(),
    queryFn: getMyMembershipRequestApi,
    enabled: isAuthenticated,
  });

  const clubsParams = {
    page,
    limit: PAGE_SIZE,
    isActive: true,
    ...(search && { search }),
    ...(cityFilter && { city: cityFilter }),
  };

  const { data: clubsData, isLoading } = useQuery({
    queryKey: queryKeys.clubs.list(clubsParams as Record<string, unknown>),
    queryFn: () => getClubsApi(clubsParams),
    enabled: isAuthenticated,
  });

  const joinMutation = useMutation({
    mutationFn: ({ clubId, msg }: { clubId: number; msg: string }) =>
      createMembershipRequestApi(clubId, msg ? { message: msg } : undefined),
    onSuccess: () => {
      toast.success('Demande envoyée ! Le club examinera votre demande.');
      setClubToJoin(null);
      setMessage('');
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.myMembershipRequest() });
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Erreur lors de l'envoi de la demande");
    },
  });

  const clubs = clubsData?.data ?? [];
  const total = clubsData?.meta?.total ?? 0;
  const totalPages = clubsData?.meta?.totalPages ?? 1;

  return (
    <>
      {/* Demande en attente */}
      {pendingRequest && (
        <div className='mb-6 flex items-center gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-5'>
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100'>
            <Clock className='h-5 w-5 text-amber-600' />
          </div>
          <div className='flex-1'>
            <p className='font-semibold text-amber-900'>Demande en attente</p>
            <p className='text-sm text-amber-700'>
              Vous avez une demande d&apos;adhésion en cours pour{' '}
              <span className='font-bold'>{pendingRequest.club?.name}</span>
              {pendingRequest.club?.city ? ` (${pendingRequest.club.city})` : ''}. Le club examinera votre demande.
            </p>
          </div>
          <a
            href={`${FRONT_URL}/clubs/${pendingRequest.club?.id}`}
            target='_blank'
            rel='noopener noreferrer'
            className='shrink-0 text-amber-600 hover:text-amber-700'
            title='Voir la fiche du club'
          >
            <ExternalLink className='h-4 w-4' />
          </a>
        </div>
      )}

      {/* Filters */}
      <div className='mb-6 flex flex-col gap-4 sm:flex-row'>
        <div className='relative flex-1'>
          <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
          <input
            type='text'
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder='Rechercher par nom, alias…'
            className='w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-4 text-sm focus:border-orange-400 focus:outline-none'
          />
        </div>
        <input
          type='text'
          value={cityFilter}
          onChange={e => {
            setSearchParams(
              prev => {
                const next = new URLSearchParams(prev);
                if (e.target.value) next.set('city', e.target.value);
                else next.delete('city');
                next.delete('page');
                return next;
              },
              { replace: true },
            );
          }}
          placeholder='Filtrer par ville…'
          className='w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-orange-400 focus:outline-none sm:w-52'
        />
      </div>

      {/* Club list */}
      {isLoading ? (
        <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
          {Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <div key={i} className='h-44 animate-pulse rounded-2xl bg-slate-200' />
          ))}
        </div>
      ) : clubs.length === 0 ? (
        <div className='rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-sm text-slate-500'>
          Aucun club trouvé
        </div>
      ) : (
        <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
          {clubs.map(club => {
            const isPendingForThis = pendingRequest?.club?.id === club.id;
            return (
              <div
                key={club.id}
                className='flex flex-col rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-orange-300 hover:shadow-md'
              >
                <div className='mb-4 flex items-start gap-3'>
                  <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-linear-to-br from-emerald-500 to-green-600 text-sm font-extrabold text-white'>
                    {club.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className='min-w-0 flex-1'>
                    <p className='truncate font-bold text-slate-900'>{club.name}</p>
                    {club.alias && <p className='text-xs text-slate-400'>@{club.alias}</p>}
                  </div>
                </div>

                <div className='mb-4 space-y-1.5 text-xs text-slate-500'>
                  {(club.city || club.department) && (
                    <div className='flex items-center gap-1.5'>
                      <MapPin className='h-3.5 w-3.5 shrink-0' />
                      {[club.city, club.department].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {club.email && (
                    <div className='flex items-center gap-1.5 truncate'>
                      <Mail className='h-3.5 w-3.5 shrink-0' />
                      {club.email}
                    </div>
                  )}
                </div>

                <div className='mt-auto flex items-center gap-2'>
                  <a
                    href={`${FRONT_URL}/clubs/${club.id}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50'
                  >
                    <ExternalLink className='h-3.5 w-3.5' />
                    Fiche
                  </a>
                  {isPendingForThis ? (
                    <span className='flex flex-1 items-center justify-center gap-1 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700'>
                      <Clock className='h-3.5 w-3.5' />
                      En attente
                    </span>
                  ) : (
                    <button
                      onClick={() => setClubToJoin(club)}
                      disabled={!!pendingRequest}
                      aria-label={`Rejoindre ${club.name}`}
                      className='flex flex-1 items-center justify-center gap-1 rounded-lg bg-orange-600 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-40'
                    >
                      <Users className='h-3.5 w-3.5' />
                      Rejoindre
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='mt-6 flex items-center justify-between'>
          <p className='text-sm text-slate-500'>
            {total} club{total > 1 ? 's' : ''}
          </p>
          <div className='flex items-center gap-1'>
            <button
              onClick={() => setParam('page', String(Math.max(1, page - 1)))}
              disabled={page === 1}
              aria-label='Page précédente'
              className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
            >
              <ChevronLeft className='h-4 w-4' />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => setParam('page', String(p))}
                aria-current={p === page ? 'page' : undefined}
                aria-label={`Page ${p}`}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                  p === page ? 'bg-orange-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setParam('page', String(Math.min(totalPages, page + 1)))}
              disabled={page >= totalPages}
              aria-label='Page suivante'
              className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
            >
              <ChevronRight className='h-4 w-4' />
            </button>
          </div>
        </div>
      )}

      {/* Modal: demande d'adhésion */}
      <Dialog
        open={!!clubToJoin}
        onClose={() => {
          setClubToJoin(null);
          setMessage('');
        }}
        title={`Rejoindre ${clubToJoin?.name ?? ''}`}
        className={'w-full max-w-xl px-5 py-3'}
      >
        <p className='mb-5 text-sm text-slate-500'>Votre demande sera examinée par les administrateurs du club.</p>
        <div className='mb-5'>
          <label className='mb-1.5 block text-xs font-semibold text-slate-700'>Message (facultatif)</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder='Présentez-vous ou expliquez votre démarche…'
            className='w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-orange-400 focus:outline-none'
          />
        </div>
        <div className='flex justify-end gap-3'>
          <button
            onClick={() => {
              setClubToJoin(null);
              setMessage('');
            }}
            disabled={joinMutation.isPending}
            className='rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50'
          >
            Annuler
          </button>
          <button
            onClick={() => clubToJoin && joinMutation.mutate({ clubId: clubToJoin.id, msg: message })}
            disabled={joinMutation.isPending}
            className='rounded-xl bg-orange-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-orange-700 disabled:opacity-50'
          >
            {joinMutation.isPending ? 'Envoi…' : 'Envoyer la demande'}
          </button>
        </div>
      </Dialog>
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────

export default function PlayerClub() {
  const user = useAuthStore(s => s.user);
  const clubId = user?.clubId ?? null;

  return (
    <DashboardLayout sidebarConfig={playerSidebar}>
      <div className='p-8'>
        {clubId ? (
          <>
            <PageHeader
              title='Mon Club'
              subtitle='Votre club et votre statut'
              actions={
                <div className='flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-2'>
                  <Building2 className='h-5 w-5 text-emerald-600' />
                  <span className='text-sm font-bold text-emerald-700'>Membre actif</span>
                </div>
              }
            />
            <MyClubView clubId={clubId} />
          </>
        ) : (
          <>
            <PageHeader
              title='Trouver un club'
              subtitle="Recherchez un club et envoyez une demande d'adhésion"
              actions={
                <div className='flex items-center gap-2 rounded-xl bg-slate-100 px-4 py-2'>
                  <X className='h-4 w-4 text-slate-500' />
                  <span className='text-sm font-semibold text-slate-600'>Sans club</span>
                </div>
              }
            />
            <ClubSearchView />
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
